import React, { ReactNode, useState } from 'react'

import { Box } from '../box'
import { CalendarSheet } from '../calendar-sheet'
import { DateTimeInput } from '../date-time-input'
import { Popover, PopoverBackdrop, PopoverContent } from '../popover'
import { TimePicker } from '../time-picker'
import { maskDateTime, parseClampedDateTime } from './dateTimeMask'

export type DateTimePickerChangeSource = 'date' | 'time' | 'text'

export interface DateTimePickerProps {
    value?: Date | null
    onChange: (date: Date, source: DateTimePickerChangeSource) => void
    onClear?: () => void
    placeholder?: string
    label?: string
    required?: boolean
    isDateDisabled?: (date: Date) => boolean
    disabled?: boolean
    isInvalid?: boolean
    tooltip?: string
    className?: string
    testID?: string
    renderTrigger?: (open: () => void) => ReactNode
}

const pad2 = (value: number): string => value.toString().padStart(2, '0')

const formatDateTime = (date: Date): string =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(
        date.getHours(),
    )}:${pad2(date.getMinutes())}`

type PickerView = 'date' | 'time'

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
    value,
    onChange,
    onClear,
    placeholder,
    label,
    required,
    isDateDisabled,
    disabled = false,
    isInvalid = false,
    tooltip,
    className,
    testID,
    renderTrigger,
}) => {
    const current = value ?? null
    const valueTime = current ? current.getTime() : null

    const [isOpen, setIsOpen] = useState(false)
    const [view, setView] = useState<PickerView>('date')
    const [viewMonth, setViewMonth] = useState<Date>(current ?? new Date())
    const [text, setText] = useState<string>(current ? formatDateTime(current) : '')

    const [prevTime, setPrevTime] = useState<number | null>(valueTime)
    if (valueTime !== prevTime) {
        setPrevTime(valueTime)
        setText(current ? formatDateTime(current) : '')
    }

    const clearValue = () => {
        setText('')
        onClear?.()
    }

    const commitText = () => {
        if (onClear && text.trim() === '') {
            if (current) {
                onClear()
            }
            return
        }
        const parsed = parseClampedDateTime(text)
        if (parsed) {
            onChange(parsed, 'text')
        } else {
            setText(current ? formatDateTime(current) : '')
        }
    }

    // Reformat only while appending; on deletion / mid-string edits leave the raw
    // text so the caret does not jump. The value is fully clamped on blur/commit.
    const handleChangeText = (next: string) => {
        setText(next.length > text.length ? maskDateTime(next) : next)
    }

    const commitDate = (day: Date) => {
        const next = new Date(day)
        if (current) {
            next.setHours(current.getHours(), current.getMinutes(), 0, 0)
        }
        onChange(next, 'date')
        setIsOpen(false)
    }

    const commitHours = (hours: number) => {
        const next = new Date(current ?? new Date())
        next.setHours(hours)
        onChange(next, 'time')
    }

    const commitMinutes = (minutes: number) => {
        const next = new Date(current ?? new Date())
        next.setMinutes(minutes)
        onChange(next, 'time')
    }

    const open = () => {
        setView('date')
        setViewMonth(current ?? new Date())
        setIsOpen(isOpenNow => !isOpenNow)
    }

    return (
        <Popover
            isOpen={isOpen && !disabled}
            onClose={() => setIsOpen(false)}
            trapFocus={false}
            focusScope={false}
            placement='bottom left'
            trigger={triggerProps => (
                <Box {...triggerProps} className={className}>
                    {renderTrigger ? (
                        renderTrigger(open)
                    ) : (
                        <DateTimeInput
                            type='date'
                            label={label}
                            required={required}
                            editable={!disabled}
                            value={text}
                            placeholder={placeholder}
                            tooltip={tooltip}
                            state={disabled ? 'disabled' : isInvalid ? 'error' : 'default'}
                            className={className}
                            testID={testID}
                            onChangeText={handleChangeText}
                            onSubmit={commitText}
                            onClear={onClear && current ? clearValue : undefined}
                            onIconPress={open}
                        />
                    )}
                </Box>
            )}
        >
            <PopoverBackdrop />
            <PopoverContent className='min-w-[256px] items-center rounded border border-solid border-border-subtle bg-background-elevation p-0'>
                {view === 'date' ? (
                    <CalendarSheet
                        month={viewMonth}
                        mode='default'
                        selectedDate={current}
                        today={new Date()}
                        isDateDisabled={isDateDisabled}
                        onPrevMonth={() =>
                            setViewMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))
                        }
                        onNextMonth={() =>
                            setViewMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))
                        }
                        onSelectDate={commitDate}
                        onTimePress={() => setView('time')}
                    />
                ) : (
                    <Box className='items-center'>
                        <TimePicker
                            hours={current?.getHours() ?? 0}
                            minutes={current?.getMinutes() ?? 0}
                            onHoursChange={commitHours}
                            onMinutesChange={commitMinutes}
                            onCalendarPress={() => setView('date')}
                        />
                    </Box>
                )}
            </PopoverContent>
        </Popover>
    )
}

DateTimePicker.displayName = 'DateTimePicker'
